import { useEffect, useRef } from 'react'

/**
 * Keep the keyboard inside an open dialog (OPS-002, PLAN §24 a11y).
 *
 * Without this, Tab walks out of a modal and into the page behind it: the
 * focus ring vanishes under the backdrop, and the next Enter presses a button
 * nobody can see. A sighted mouse user never meets it, which is why it
 * survived 1A-4 and why it is worth a shared hook rather than two copies.
 *
 * Three things, and all three are the specification rather than polish:
 *
 * * **Tab cycles.** Last element wraps to the first, Shift+Tab the other way.
 * * **Escape closes**, from anywhere inside.
 * * **Focus comes back** to whatever opened the dialog. Landing at the top of
 *   the document instead is how a keyboard user loses their place on a long
 *   album page.
 *
 * `aria-hidden` is not set on the rest of the page: the backdrop already
 * blocks the pointer, and hiding a subtree that still contains the focus for
 * one frame is worse for a screen reader than not hiding it at all.
 */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'details > summary',
  '[tabindex]:not([tabindex="-1"])',
]
  .map((selector) => `${selector}:not([hidden]):not([aria-hidden="true"])`)
  .join(',')

/**
 * Deliberately no `offsetParent` or `getClientRects` visibility check.
 *
 * Both are layout, and there is no layout in jsdom — every element reports
 * itself invisible there, so a filter built on them makes the trap a no-op in
 * every test while appearing to work in the browser. `getComputedStyle` has
 * the same problem. The selector already excludes `disabled`, `hidden` and
 * `aria-hidden`, and a dialog's subtree holds only its own controls, so there
 * is nothing left for a visibility check to usefully remove.
 */
export function focusableWithin(node) {
  if (!node) return []
  return Array.from(node.querySelectorAll(FOCUSABLE))
}

export function useFocusTrap(active, onClose) {
  const ref = useRef(null)
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  useEffect(() => {
    if (!active) return undefined
    const node = ref.current
    const restoreTo = document.activeElement

    const first = focusableWithin(node)[0]
    if (first) first.focus()
    else node?.focus?.()

    function onKeyDown(event) {
      if (event.key === 'Escape') {
        event.stopPropagation()
        closeRef.current?.()
        return
      }
      if (event.key !== 'Tab') return
      const items = focusableWithin(node)
      if (items.length === 0) {
        event.preventDefault()
        return
      }
      const edge = event.shiftKey ? items[0] : items[items.length - 1]
      // `contains` and not an index lookup: focus may be on the dialog itself
      // when it has no focusable children yet, and that is still inside.
      if (document.activeElement === edge || !node?.contains(document.activeElement)) {
        event.preventDefault()
        ;(event.shiftKey ? items[items.length - 1] : items[0]).focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      if (restoreTo instanceof HTMLElement && document.contains(restoreTo)) restoreTo.focus()
    }
  }, [active])

  return ref
}
